/**
 * KaizenCalc - Efficient calculator expression parser
 * Optimized for React Native with minimal allocations
 */

import type { NodeType, ParseNode, ParseResult } from './ParseTree';

// Re-export types for convenience
export type { NodeType, ParseNode, ParseResult };

type TokenType =
  | 'number'
  | 'identifier'
  | 'operator'
  | 'lparen'
  | 'rparen'
  | 'comma'
  | 'eof';

interface Token {
  type: TokenType;
  value: string | number;
  position: number;
}

export class ParseError extends Error {
  constructor(message: string, public position: number) {
    super(`Parse error at position ${position}: ${message}`);
    this.name = 'ParseError';
  }
}

/**
 * Efficient tokenizer for calculator expressions
 */
class Tokenizer {
  private pos = 0;
  private currentChar: string | null;

  constructor(private input: string) {
    this.currentChar = input.length > 0 ? input[0] : null;
  }

  private advance(): void {
    this.pos++;
    this.currentChar = this.pos < this.input.length ? this.input[this.pos] : null;
  }

  private skipWhitespace(): void {
    while (this.currentChar !== null && /\s/.test(this.currentChar)) {
      this.advance();
    }
  }

  private readNumber(): Token {
    const start = this.pos;
    let hasDecimal = false;

    while (this.currentChar !== null && /[0-9.]/.test(this.currentChar)) {
      if (this.currentChar === '.') {
        if (hasDecimal) {
          // Second decimal point - invalid number
          throw new ParseError(`Invalid number format: multiple decimal points`, start);
        }
        hasDecimal = true;
      }
      this.advance();
    }

    const numStr = this.input.substring(start, this.pos);
    const value = parseFloat(numStr);
    if (isNaN(value)) {
      throw new ParseError(`Invalid number: ${numStr}`, start);
    }

    return { type: 'number', value, position: start };
  }

  private readIdentifier(): Token {
    const start = this.pos;

    while (
      this.currentChar !== null
      && /[a-zA-Z_0-9]/.test(this.currentChar)
    ) {
      this.advance();
    }

    const identifier = this.input.substring(start, this.pos);
    return { type: 'identifier', value: identifier, position: start };
  }

  public getNextToken(): Token {
    this.skipWhitespace();

    if (this.currentChar === null) {
      return { type: 'eof', value: '', position: this.pos };
    }

    const position = this.pos;

    // Numbers (including .5 format)
    if (/[0-9]/.test(this.currentChar)) {
      return this.readNumber();
    }

    // Decimal numbers starting with dot (e.g., .5)
    if (this.currentChar === '.' && this.pos + 1 < this.input.length && /[0-9]/.test(this.input[this.pos + 1])) {
      return this.readNumber();
    }

    // Identifiers (parameters and functions)
    if (/[a-zA-Z_]/.test(this.currentChar)) {
      return this.readIdentifier();
    }

    // Two-character operators
    if (this.currentChar === '>' && this.pos + 1 < this.input.length && this.input[this.pos + 1] === '=') {
      this.advance();
      this.advance();
      return { type: 'operator', value: '>=', position };
    }
    if (this.currentChar === '<' && this.pos + 1 < this.input.length && this.input[this.pos + 1] === '=') {
      this.advance();
      this.advance();
      return { type: 'operator', value: '<=', position };
    }
    if (this.currentChar === '=' && this.pos + 1 < this.input.length && this.input[this.pos + 1] === '=') {
      this.advance();
      this.advance();
      return { type: 'operator', value: '==', position };
    }
    if (this.currentChar === '!' && this.pos + 1 < this.input.length && this.input[this.pos + 1] === '=') {
      this.advance();
      this.advance();
      return { type: 'operator', value: '!=', position };
    }

    // Single-character operators
    if ('+-*/^><'.includes(this.currentChar)) {
      const op = this.currentChar;
      this.advance();
      return { type: 'operator', value: op, position };
    }

    // Parentheses
    if (this.currentChar === '(') {
      this.advance();
      return { type: 'lparen', value: '(', position };
    }

    if (this.currentChar === ')') {
      this.advance();
      return { type: 'rparen', value: ')', position };
    }

    // Comma
    if (this.currentChar === ',') {
      this.advance();
      return { type: 'comma', value: ',', position };
    }

    throw new ParseError(
      `Unexpected character: ${this.currentChar}`,
      position,
    );
  }

  public getAllTokens(): Token[] {
    const tokens: Token[] = [];
    let token: Token;

    do {
      token = this.getNextToken();
      tokens.push(token);
    } while (token.type !== 'eof');

    return tokens;
  }
}

/**
 * Recursive descent parser for calculator expressions
 */
class Parser {
  private tokenizer: Tokenizer;
  private currentToken: Token;

  constructor(input: string) {
    this.tokenizer = new Tokenizer(input);
    this.currentToken = this.tokenizer.getNextToken();
  }

  private advance(): Token {
    const token = this.currentToken;
    if (token.type !== 'eof') {
      this.currentToken = this.tokenizer.getNextToken();
    }
    return token;
  }

  private expect(type: TokenType, value?: string): Token {
    const token = this.currentToken;
    if (token.type !== type || (value != null && token.value !== value)) {
      throw new ParseError(
        `Expected ${type}${value ? ` '${value}'` : ''} but got ${token.type}${token.value ? ` '${token.value}'` : ''}`,
        token.position,
      );
    }
    return this.advance();
  }

  /**
   * Constant folding optimization
   * Evaluates constant expressions at parse time
   * Examples: 2 + 3 => 5, 10 / 5 => 2, -5 => -5
   */
  private foldConstant(node: ParseNode): ParseNode {
    // Can't fold primitives
    if (typeof node === 'number' || typeof node === 'string') {
      return node;
    }

    const type = node[0];

    // Binary operations: fold if both operands are numbers
    if (
      type === 'add'
      || type === 'subtract'
      || type === 'multiply'
      || type === 'divide'
      || type === 'power'
    ) {
      const left = node[1] as ParseNode;
      const right = node[2] as ParseNode;

      if (typeof left === 'number' && typeof right === 'number') {
        let result: number;

        switch (type) {
          case 'add':
            result = left + right;
            break;
          case 'subtract':
            result = left - right;
            break;
          case 'multiply':
            result = left * right;
            break;
          case 'divide':
            result = left / right;
            break;
          case 'power':
            result = left ** right;
            break;
          default:
            return node;
        }

        return result; // Return number directly
      }
    }

    // Unary operations: fold if operand is a number
    if (type === 'negate' || type === 'positive') {
      const operand = node[1] as ParseNode;

      if (typeof operand === 'number') {
        const result = type === 'negate' ? -operand : operand;
        return result; // Return number directly (positive is no-op)
      }
    }

    // No folding possible
    return node;
  }

  /**
   * Parse entry point
   * expression := comparison
   */
  public parse(): ParseNode {
    const result = this.parseComparison();
    if (this.currentToken.type !== 'eof') {
      throw new ParseError(
        `Unexpected token: ${this.currentToken.value}`,
        this.currentToken.position,
      );
    }
    return result;
  }

  /**
   * comparison := additive (('>' | '<' | '>=' | '<=' | '==' | '!=') additive)*
   */
  private parseComparison(): ParseNode {
    let left = this.parseAdditive();

    while (this.currentToken.type === 'operator') {
      const op = this.currentToken.value as string;
      let nodeType: NodeType | null = null;

      switch (op) {
        case '>':
          nodeType = 'gt';
          break;
        case '<':
          nodeType = 'lt';
          break;
        case '>=':
          nodeType = 'gte';
          break;
        case '<=':
          nodeType = 'lte';
          break;
        case '==':
          nodeType = 'eq';
          break;
        case '!=':
          nodeType = 'neq';
          break;
        default:
          // Not a comparison operator, return what we have
          return left;
      }

      this.advance();
      const right = this.parseAdditive();
      left = [nodeType, left, right] as ParseNode;
    }

    return left;
  }

  /**
   * additive := multiplicative (('+' | '-') multiplicative)*
   */
  private parseAdditive(): ParseNode {
    let left = this.parseMultiplicative();

    while (
      this.currentToken.type === 'operator'
      && (this.currentToken.value === '+' || this.currentToken.value === '-')
    ) {
      const op = this.currentToken.value as string;
      this.advance();
      const right = this.parseMultiplicative();
      left = this.foldConstant([op === '+' ? 'add' : 'subtract', left, right] as ParseNode);
    }

    return left;
  }

  /**
   * multiplicative := power (('*' | '/') power)*
   */
  private parseMultiplicative(): ParseNode {
    let left = this.parsePower();

    while (
      this.currentToken.type === 'operator'
      && (this.currentToken.value === '*' || this.currentToken.value === '/')
    ) {
      const op = this.currentToken.value as string;
      this.advance();
      const right = this.parsePower();
      left = this.foldConstant([op === '*' ? 'multiply' : 'divide', left, right] as ParseNode);
    }

    return left;
  }

  /**
   * power := unary ('^' unary)*
   * Note: Right-associative for exponentiation
   */
  private parsePower(): ParseNode {
    let left = this.parseUnary();

    if (this.currentToken.type === 'operator' && this.currentToken.value === '^') {
      this.advance();
      const right = this.parsePower(); // Right-associative
      return this.foldConstant(['power', left, right] as ParseNode);
    }

    return left;
  }

  /**
   * unary := ('+' | '-')? primary
   */
  private parseUnary(): ParseNode {
    if (this.currentToken.type === 'operator') {
      if (this.currentToken.value === '-') {
        this.advance();
        const operand = this.parseUnary();
        return this.foldConstant(['negate', operand] as ParseNode);
      } else if (this.currentToken.value === '+') {
        this.advance();
        const operand = this.parseUnary();
        return this.foldConstant(['positive', operand] as ParseNode);
      }
    }

    return this.parsePrimary();
  }

  /**
   * primary := number | identifier | functionCall | '(' expression ')'
   */
  private parsePrimary(): ParseNode {
    const token = this.currentToken;

    // Number literal - return number directly
    if (token.type === 'number') {
      this.advance();
      return token.value as number;
    }

    // Identifier (variable or function)
    if (token.type === 'identifier') {
      const name = token.value as string;
      this.advance();

      // Check if it's a function call
      if (this.currentToken.type === 'lparen') {
        return this.parseFunctionCall(name);
      }

      // It's a parameter reference - return string directly
      return name;
    }

    // Grouped expression (parentheses for precedence override)
    // No need to create a 'group' node - just return the inner expression
    if (token.type === 'lparen') {
      this.advance();
      const expr = this.parseComparison();
      this.expect('rparen');
      return expr;
    }

    throw new ParseError(
      `Unexpected token: ${token.value}`,
      token.position,
    );
  }

  /**
   * functionCall := identifier '(' (expression (',' expression)*)? ')'
   */
  private parseFunctionCall(name: string): ParseNode {
    this.expect('lparen');

    const args: ParseNode[] = [];

    // Empty function call
    if (this.currentToken.type === 'rparen') {
      this.advance();
      return ['call', name] as ParseNode;
    }

    // Parse arguments
    args.push(this.parseComparison());

    while (this.currentToken.type === 'comma') {
      this.advance();
      args.push(this.parseComparison());
    }

    this.expect('rparen');

    return ['call', name, ...args] as ParseNode;
  }
}

/**
 * Parse a calculator expression into an AST
 */
export function parse(input: string): ParseNode {
  const parser = new Parser(input);
  return parser.parse();
}

/**
 * Extract parameter and function references from a parse tree
 */
export function extractReferencesFromTree(
  tree: ParseNode,
): { parameters: string[]; functions: string[] } {
  const parameters = new Set<string>();
  const functions = new Set<string>();

  function walk(node: ParseNode): void {
    // Handle primitive nodes
    if (typeof node === 'number') {
      return; // Numbers have no references
    }
    if (typeof node === 'string') {
      parameters.add(node); // Strings are parameter names
      return;
    }

    // Handle operation nodes
    const [type, ...rest] = node;

    if (type === 'call') {
      functions.add(rest[0] as string);
      // Walk function arguments (skip function name at rest[0])
      for (let i = 1; i < rest.length; i++) {
        walk(rest[i] as ParseNode);
      }
    } else {
      // Walk all children for other operations
      for (const child of rest) {
        walk(child as ParseNode);
      }
    }
  }

  walk(tree);

  return {
    parameters: Array.from(parameters).sort(),
    functions: Array.from(functions).sort(),
  };
}

/**
 * Extract parameter and function references from an expression string
 */
export function extractReferences(
  input: string,
): { parameters: string[]; functions: string[] } {
  const tree = parse(input);
  return extractReferencesFromTree(tree);
}

/**
 * Parse an expression and return parse tree with extracted dependencies
 *
 * This is the recommended high-level API that gives you everything you need:
 * - parseTree: The AST for evaluation
 * - parameters: Sorted list of parameter names used
 * - functions: Sorted list of function names used
 *
 * @example
 * const result = parseExpression('max(a, b) + c * 2')
 * // result = {
 * //   parseTree: ['add', ['call', 'max', ...], ['multiply', ...]],
 * //   parameters: ['a', 'b', 'c'],
 * //   functions: ['max']
 * // }
 *
 * // Then evaluate:
 * const value = evaluate(result.parseTree, { a: 5, b: 10, c: 3 }, { max: Math.max })
 */
export function parseExpression(input: string): ParseResult {
  const tree = parse(input);
  const refs = extractReferencesFromTree(tree);
  return {
    parseTree: tree,
    parameters: refs.parameters,
    functions: refs.functions,
  };
}
